import React from "react";
import { motion } from "framer-motion";
import {
  FaBrain,
  FaLaptopCode,
  FaUsers,
  FaGraduationCap,
} from "react-icons/fa";

import heroImg from "../assets/uploads/school.jpg";

const outcomes = [
  {
    icon: <FaGraduationCap />,
    title: "Academic Excellence",
    desc: "Building strong subject knowledge and achieving proficiency in academics through a well-planned curriculum.",
  },
  {
    icon: <FaBrain />,
    title: "Critical Thinking",
    desc: "Encouraging students to think critically, analyze situations, and solve real-life problems with confidence.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Technology Integration",
    desc: "Helping students become familiar with modern technology and digital learning tools.",
  },
  {
    icon: <FaUsers />,
    title: "21st Century Skills",
    desc: "Developing communication, collaboration, leadership, creativity, and social responsibility.",
  },
];

export default function SchoolCurriculum() {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section
        className="relative h-[55vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="absolute inset-0 bg-[#07294d]/75"></div>

        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative text-center text-white px-6"
        >
          <p className="uppercase tracking-[6px] text-[#d4af37] font-semibold mb-4">
            Cambridge International School
          </p>

          <h1 className="text-5xl md:text-6xl font-black">
            School Curriculum
          </h1>
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-[#07294d] mb-6">
              Curriculum Philosophy
            </h2>

            <div className="w-24 h-1 bg-[#d4af37] mx-auto mb-8"></div>
          </div>

          <div className="bg-[#f8fafc] rounded-3xl shadow-xl p-10 lg:p-14">

            <p className="text-gray-700 leading-9 text-lg mb-8">
              A good and effective curriculum displays a planned, purposeful,
              progressive, and systematic process to create positive
              improvements in the educational system. It focuses not only on
              the development of learners and teachers but also contributes
              significantly to the growth of society.
            </p>

            <p className="text-gray-700 leading-9 text-lg mb-8">
              Curriculum planning involves implementing diverse instructional
              strategies and organizational methods that help students achieve
              the best possible learning outcomes. It plays a vital role in
              guiding meaningful educational change at both institutional and
              societal levels.
            </p>

            <p className="text-gray-700 leading-9 text-lg">
              At Cambridge International School, Nakodar, our learner-centric
              curriculum is carefully designed to make measurable and lasting
              impacts on students' academic, personal, and social development.
            </p>

          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="pb-24 px-6 bg-[#f7f9fc]">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-16">

            <h2 className="text-4xl font-black text-[#07294d]">
              Learning Outcomes
            </h2>

            <p className="text-gray-600 mt-4 text-lg">
              We prepare students with knowledge, values, and future-ready
              skills.
            </p>

          </div>

          <div className="grid md:grid-cols-2 gap-8">

            {outcomes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#07294d] text-white flex items-center justify-center text-3xl mb-6">
                  {item.icon}
                </div>

                <h3 className="text-2xl font-bold text-[#07294d] mb-4">
                  {item.title}
                </h3>

                <p className="text-gray-600 leading-8">
                  {item.desc}
                </p>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#07294d] text-white text-center px-6">

        <h2 className="text-4xl font-black mb-6">
          Inspiring Lifelong Learners
        </h2>

        <p className="max-w-3xl mx-auto text-lg leading-8 text-gray-200">
          Our curriculum empowers students with academic excellence,
          creativity, confidence, leadership, and the essential skills needed
          to succeed in the rapidly changing world.
        </p>

      </section>

    </div>
  );
}